import { useLocation, useParams } from '@solidjs/router';
import { createEffect, createMemo, Show, useContext } from 'solid-js';
import clsx from 'clsx';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { Section } from '../../grid-system/section/section';
import { ErrorCatcher } from '../../tools/error-catcher';
import { PageCacheType } from '../../types/shared';
import { LockedContent } from '../../ui-components/layouts/locked-content';
import { Text } from '../../ui-components/text/text';
import { NotFound } from '../404/404';
import { SeoSettings } from '../seo-settings/seo-settings';
import { ShareAndSave } from '../share-and-save/share-and-save';
import { ExpandableProductContainer } from './expandable-product-container/expandable-product-container';
import { GET_PRODUCT_QUERY } from './fetch-product';
import { ProductDescription } from './product-description/product-description';
import { ProductFamily } from './product-family/product-family';
import { ProductContactCard, ProductFurtherLinks } from './product-further-links/product-further-links';
import { ProductGallery } from './product-gallery';
import { ProductHeader } from './product-header';
import { ProductSpecifications } from './product-specifications/product-specifications';
import { ProductSupplements } from './product-supplements/product-supplements';
import { ProductData, DocumentType } from './product-types';
import { ProductVideo } from './product-video/product-video';
import ProductShopToolbar from './product-shop-toolbar/product-shop-toolbar';
import { HCPWarning } from '../hcp-warning/hcp-warning';
import { legacyDocuments } from './legacy-documents';
import { UserWarning } from '../user-warning/user-warning';
import { ProductSchema } from '../structured-data';
import { Breadcrumb } from '../breadcrumb/breadcrumb';

export const specificationComponentId = 'specification-component';


export const Product = () => {
    const params = useParams<{ productSlug: string }>();
    const location = useLocation() as { state?: { productsPageQuery?: string }, pathname: string };

    const {
        setCacheType,
        createCachedResource,
        localize,
        setStatusCode,
        userState,
        pageRegistration,
        siteInfo,
        appStore,
        productInformation,
        productsListingPage,
    } = useContext(AppContext);

    const [productContent] = createCachedResource(GET_PRODUCT_QUERY, () => ({ key: `/product/${params.productSlug}` }), true);
    const productData = createMemo((): ProductData => productContent()?.product?.content);

    const isHcp = () => {
        if (checkHcp()) {
            setCacheType(PageCacheType.none);
            return true;
        }
        setCacheType(PageCacheType.defaultCache);
        return false;
    };

    const checkHcp = () => {
        /**
         * "appStore.isHcp" behaves like a ratchet, once set to true, it can't be set to false,
         * unless the homepage is visited (which is a "patients" page, not a HCP page)
         */
        if (location.pathname === '/') {
            return false;
        }

        if (appStore.isHcp()) {
            return true;
        }

        return Boolean(productData()?.isHealthcareProfessional);
    };

    const setHcp = () => {
        const isHcp = checkHcp();
        appStore.setHcp(isHcp);
        return null;
    };

    setHcp();

    createEffect(() => {
        setHcp();
    });

    const isHiddenForPublic = () => {
        return checkHcp()
            && !productData().isVisibleForPublic
            && !userState.user
            && pageRegistration.url !== '';
    };

    const checkNotFound = () => {
        if (!productData() || hide()) {
            setStatusCode(404);
            return true;
        } else {
            setStatusCode(200);
            return false;
        }
    };

    const filterDocuments = (documents: DocumentType[]) => {
        try {
            if (!documents || !Array.isArray(documents)){
                return [];
            }

            return documents.filter((document) => {
                if (!document?.file?.url) {
                    return false;
                }

                if (legacyDocuments.includes(document.file.name)) {
                    return false;
                }

                return true;
            });
        } catch (error) {
            return [];
        }
    };

    const parseEscapedBreaks = (text: string) => {
        return text.replace(/&lt;br\/&gt;/g, '<br />');
    };

    const hide = () => {
        // On draft we always want to show the product
        if (siteInfo.target === 'draft') return false;

        if (productData()?.hideOnWeb) return true;
        if (productData()?.hideForLoggedOutUsersOnCareSite && !userState.user) return true;

        return false;
    };

    const breadcrumbPages = createMemo(() => {
        const permalink = productData()?.permalink ?? '';
        if (!permalink || !productsListingPage?.url) {
            return [];
        }
        return [
            {
                postTitle: productsListingPage.title ?? '',
                shortTitle: '',
                permalink: productsListingPage.url,
            },
            {
                postTitle: productData()?.post_title ?? '',
                shortTitle: '',
                permalink,
            },
        ];
    });

    return (
        <ErrorCatcher componentName="Product">
            <article
                class={clsx(
                    '[container-type:inline-size] print:hidden',
                    siteInfo.siteType === 'atos-care' && 'mt-cvi-10'
                )}
            >
                <Show when={checkNotFound() && !productContent.loading}>
                    <NotFound />
                </Show>

                <Show when={!checkNotFound() && !productContent.loading}>
                    <UserWarning isUserContent={productData()?.isUserContent ? true : false} />
                    <HCPWarning isHcp={isHcp()} />
                    <SeoSettings seo={productData().seo} title={productData().post_title}></SeoSettings>
                    <ProductSchema product={productData()} />
                    <Show when={breadcrumbPages().length > 1}>
                        <Breadcrumb
                            pages={breadcrumbPages()}
                            url={productData()?.permalink}
                        />
                    </Show>
                    <Section
                        templateShorthand={[12]}
                        widthType="bgFull"
                        customCss={'padding-top: var(--spacing-cvi-section-default); padding-bottom: 0;'}
                    >
                        <div class="product-page-layout">
                            <div class="product-page-layout__right">
                                <header class="product-page-layout__header">
                                    <ProductHeader
                                        productTitle={productData().post_title}
                                        shortDescription={productData().shortDescription}
                                        purchaseButton={productData().purchaseButton}
                                    />
                                </header>
                                <Show when={siteInfo.siteType === 'atos-care'}>
                                    <div class="product-page-layout__purchase border-t border-t-cvi-border-muted pt-cvi-8 tablet-original:mt-cvi-11">
                                        <ProductShopToolbar productData={productData()} />
                                    </div>
                                </Show>
                            </div>

                            <div class="product-page-layout__left">
                                {/* Figma 173:10733 has no "back to products" control — the breadcrumb carries that. */}
                                <div class="product-page-layout__gallery tablet-original:pb-cvi-7">
                                    <ProductGallery images={productData().images} />
                                </div>

                                <div class="product-page-layout__body flex flex-col gap-cvi-6">
                                    <ProductDescription
                                        subTitle={productData().subTitle}
                                        longDescription={productData().longDescription}
                                        features={productData().features}
                                    />
                                    <Show when={productData().videoData?.videos?.length}>
                                        <ProductVideo videoData={productData().videoData} />
                                    </Show>
                                    <ProductFamily
                                        productFamilyImage={productData().productFamilyImage}
                                        productFamilyLink={productData().productFamilyGuideLink}
                                        productLearnMoreLink={productData().productFamilyLearnMore}
                                    />

                                    <div class="flex flex-col py-cvi-10">
                                        <Show when={productData().specifications.length}>
                                            <div id={specificationComponentId}>
                                                <ExpandableProductContainer
                                                    heading={localize('specifications', 'Specifications')}
                                                    initialOpen={siteInfo.siteType === 'atos-care'}
                                                >
                                                    <ProductSpecifications product={productData()} />
                                                </ExpandableProductContainer>
                                            </div>
                                        </Show>
                                        <Show when={filterDocuments(productData()?.documents).length}>
                                            <ExpandableProductContainer heading={localize('supporting-material', 'Supporting Material')}>
                                                <ProductSupplements documents={filterDocuments(productData().documents as [DocumentType])} />
                                            </ExpandableProductContainer>
                                        </Show>
                                    </div>

                                    <ProductFurtherLinks />
                                </div>
                            </div>
                            <div class="col-span-full order-last pt-cvi-10 pb-cvi-section-spacious">
                                <ProductContactCard productTitle={productData().post_title} />

                                <div class="w-full flex gap-cvi-6">
                                    <Show when={productInformation}>
                                        <div class='pt-cvi-section-close grow' innerHTML={parseEscapedBreaks(productInformation)} />
                                    </Show>

                                    <Show when={productData()?.mcRef}>
                                        <div class="flex justify-end grow pt-cvi-section-close">
                                            <Text fontSize='body-xs'>
                                                {productData()?.mcRef}
                                            </Text>
                                        </div>
                                    </Show>
                                </div>

                                <Show when={isHiddenForPublic()}>
                                    <LockedContent />
                                </Show>
                            </div>
                        </div>
                    </Section>

                    <ShareAndSave type="product" title={productData().post_title} permalink={productData().permalink} data={productData()} />
                </Show>
            </article>
        </ErrorCatcher>
    );
};
